import {
  Button,
  FeedCard,
  Icon,
  Toast,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSWRConfig } from 'swr';
import { useQueryClient } from '@tanstack/react-query';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { updateFeedContentCache } from '../../../../common/queries/update-feed-content-cache';
import {
  contentOperations,
  contentSelectors,
} from '../../../../models/content';
import {
  trackContentCardLike,
  trackContentCardUnlike,
} from '../../../../models/content/analytics';
import { getContentLikesDataKey } from '../../../../common/use-content-likes';
import { FeedCardActionsProps } from './type';
import { programSelectors } from '../../../../models/program';
import useFeatureFlag from '../../../../common/use-feature-flag';
import { Feature } from '../../../../models/features/features';
import { useContentExpandedReactions } from '../../../../common/use-content-expanded-reactions';

function FeedCardActionLikeOnly({
  content,
  analyticsData,
  compactCounts,
  constrainWidth,
  isHidden,
}: FeedCardActionsProps) {
  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );

  const isLiked = usePatronSelector((s) =>
    contentSelectors.getContentLiked(s, { contentId: content.id })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId: content.id })
  );
  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const likeContent = (id: number) =>
    dispatch(contentOperations.likeContent(id));
  const unlikeContent = (id: number) =>
    dispatch(contentOperations.unlikeContent(id));

  const handleClick = async () => {
    const liked = !isLiked;
    if (isLiked) {
      trackContentCardUnlike(content.id, analyticsData);
      await unlikeContent(content.id);
    } else {
      trackContentCardLike(content.id, analyticsData);
      await likeContent(content.id);
    }
    // Trigger revalidation on the content likes list
    void mutate(getContentLikesDataKey(content.id));
    // Keep localStorage React Query cache in sync so state persists across page reloads
    updateFeedContentCache(queryClient, content.id, {
      liked,
      reaction_counts: {
        likes: liked ? likeCount + 1 : Math.max(0, likeCount - 1),
      },
    });
  };

  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, { contentId: content.id })
  );

  const likeActionKey = isLiked
    ? 'content.like_remove_action'
    : 'content.like_action';
  const actionLabel = t(likeActionKey, { title: contentTitle });

  const countLabel = compactCounts
    ? t('compact_number', { value: likeCount })
    : String(likeCount);

  // The cap belongs to the action row's width problem, which the overflow menu
  // does not have: it lays its items out in a `w-min` column, where a capped
  // control would ellipsize with room to spare.
  const capWidth = constrainWidth && !isHidden;

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <Button
          // `shrink` must be the literal class the row's `shrink-0` rule
          // exempts. The cap is inline because patron builds no Tailwind, so an
          // arbitrary utility resolves only while Solar emits that same value;
          // 116 is what the row leaves this control beside comment and overflow.
          className={capWidth ? 'shrink' : undefined}
          style={capWidth ? { maxWidth: 116, minWidth: 0 } : undefined}
          aria-label={actionLabel}
          label={
            expandedReactionTypesDeployed
              ? t('content.like', { count: 1 })
              : countLabel
          }
          leftIcon={<Icon size={20}>thumb_up</Icon>}
          size="compact"
          variant="text"
          selected={isLiked}
          onClick={handleClick}
        />
      </TooltipTrigger>
      <TooltipContent
        description={
          isLiked ? t('content.like_remove') : t('content.like', { count: 1 })
        }
      />
    </TooltipRoot>
  );
}

export function FeedCardActionReact({
  contentId,
  analyticsData,
  size = 'compact',
  container,
}: {
  contentId: number;
  analyticsData: IAnalyticsData;
  size?: 'compact' | 'default';
  container?: HTMLElement | null;
}) {
  const { t } = useTranslation();

  const { selectedReaction, availableReactions, setNewReaction, isError } =
    useContentExpandedReactions({
      contentId: contentId,
      analyticsData,
    });

  if (!availableReactions?.length) {
    return null;
  }

  return (
    <>
      <FeedCard.ReactionButton
        selectedReactionId={selectedReaction?.id || null}
        availableReactions={availableReactions}
        onReactionSelect={setNewReaction}
        size={size}
        portalProps={{ container }}
      />
      {isError && (
        <Toast
          title={t('content.reaction.error')}
          variant="error"
          defaultOpen
        />
      )}
    </>
  );
}

export const FeedCardActionLikeButton = (props: FeedCardActionsProps) => {
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );
  return expandedReactionTypesEnabled ? (
    <FeedCardActionReact
      contentId={props.content.id}
      analyticsData={props.analyticsData}
    />
  ) : (
    <FeedCardActionLikeOnly {...props} />
  );
};
